import * as Sentry from '@sentry/react';
import {
  EventName,
  HtmlActionElementData,
  WebsitePreviewEvent,
  WebsitePreviewEventFixHtmlSectionError,
  WebsitePreviewEventGenerateSuggestions,
} from '@superfunnel/types';
import { useCallback, useEffect } from 'react';
import { useAgentChat } from '../../../contexts/AgentChatContext';
import {
  WebsiteUpdate,
  WebsiteUpdateType,
} from '../../../contexts/WebsiteUpdaterContext';
import { PREVIEW_ROOT } from '../../../utils';

export interface UseIframeMessagingReturn {
  handlePreviewEvent: (event: MessageEvent<string>) => void;
}

interface UseIframeMessagingProps {
  id: string;
  dynamicPageId?: string;
  website?: any;
  isEditable: boolean;
  previewMode: boolean;
  ready: number;
  setReady: () => void;
  setAddFavoriteSectionModalOpen: (section: any) => void;
  setRemoveFavoriteSectionModalOpen: (state: any) => void;
  setAddSectionToCatalogModalOpen: (section: any) => void;
  setRemoveSectionFromCatalogModalOpen: (state: any) => void;
  setEditGlobalSectionModalOpen: (state: any) => void;
  setEditHtmlActionModalOpen: (data: HtmlActionElementData | undefined) => void;
  createWebsiteUpdate: any;
  handleWebsiteUpdateEvent: (wu: WebsiteUpdate) => void;
  handleGetDataEvent: (data: WebsitePreviewEvent) => void;
  handleGenerateSuggestionsEvent: (
    data: WebsitePreviewEventGenerateSuggestions,
  ) => void;
  websiteId: string;
  device: string;
  sendMessageToIframe: (message: any) => void;
  isVisualEditMode: boolean;
  setIsVisualEditMode: (mode: boolean) => void;
  onCursorMove?: (x: number, y: number) => void;
}

export function useIframeMessaging({
  id,
  dynamicPageId,
  website,
  isEditable,
  previewMode,
  ready,
  setReady,
  setAddFavoriteSectionModalOpen,
  setRemoveFavoriteSectionModalOpen,
  setAddSectionToCatalogModalOpen,
  setRemoveSectionFromCatalogModalOpen,
  setEditGlobalSectionModalOpen,
  setEditHtmlActionModalOpen,
  createWebsiteUpdate,
  handleWebsiteUpdateEvent,
  handleGetDataEvent,
  handleGenerateSuggestionsEvent,
  websiteId,
  device,
  sendMessageToIframe,
  setIsVisualEditMode,
  onCursorMove,
}: UseIframeMessagingProps): UseIframeMessagingReturn {
  const { sendMessage, addPromptContext } = useAgentChat();

  const handlePreviewEvent = useCallback(
    (event: MessageEvent<string>) => {
      if (event.origin !== PREVIEW_ROOT) return;
      if (!dynamicPageId) return;
      const iframe = (document.getElementById(id) as HTMLIFrameElement)
        ?.contentWindow;
      if (event.source !== iframe) return;

      try {
        const data: WebsitePreviewEvent = JSON.parse(event.data);
        console.log('[WEBSITE_UPDATER] event', data?.eventName, data);

        if (data.eventName === 'ready') {
          console.warn(
            '[WEBSITE_PREVIEW_EVENT] ready event received',
            data.eventName,
            !!website,
          );
          setReady();
          return;
        }

        if (data.eventName === 'getData') {
          handleGetDataEvent(data);
          return;
        }

        if (data.eventName === 'generateSuggestions') {
          handleGenerateSuggestionsEvent(
            data as WebsitePreviewEventGenerateSuggestions,
          );
          return;
        }

        if (data.eventName === 'addToFavorites') {
          setAddFavoriteSectionModalOpen(data.data.section);
          return;
        }

        if (data.eventName === 'removeFromFavorites') {
          setRemoveFavoriteSectionModalOpen({
            favoriteSectionId: data.data.favoriteSectionId,
          });
          return;
        }

        if (data.eventName === 'addToCatalog') {
          setAddSectionToCatalogModalOpen(data.data.section);
          return;
        }

        if (data.eventName === 'removeFromCatalog') {
          setRemoveSectionFromCatalogModalOpen({
            sectionTypeConfigId: data.data.sectionTypeConfigId,
            variantId: data.data.variantId,
          });
          return;
        }

        if (data.eventName === 'editGlobalSection') {
          setEditGlobalSectionModalOpen({
            open: true,
            globalSectionId: data.data.globalSectionId,
          });
          return;
        }

        if (data.eventName === 'editHtmlAction') {
          setEditHtmlActionModalOpen(data.data);
          return;
        }

        if (data.eventName === 'cursorMove') {
          // Handle cursor movement from iframe
          if (onCursorMove && data.data) {
            onCursorMove(data.data.x, data.data.y);
          }
          return;
        }

        if (data.eventName === 'fixHtmlSectionError') {
          const errorData = data as WebsitePreviewEventFixHtmlSectionError;

          // Find the section with the error
          const sections = website?.development?.sections || [];
          const section = sections.find(
            (s: any) => s.id === errorData.data.sectionId,
          );

          // Format the error message for the agent
          const errorMessage = `## HTML Section Rendering Error

**Error Details:**
- **Message:** ${errorData.data.error.message}
- **Component Stack:** ${errorData.data.error.componentStack || 'N/A'}

**Section Information:**
- **Section ID:** ${errorData.data.sectionId}
- **Section Index:** ${errorData.data.sectionIndex}
- **Section Type:** ${section?.type || 'Unknown'}

**Current HTML Content:**
\`\`\`html
${section?.content || 'No content available'}
\`\`\`

Please analyze this error and fix the HTML content to resolve the rendering issue.`;

          // Send the message to the agent
          sendMessage(errorMessage);

          return;
        }

        if ((data as any).eventName === 'elementSelected') {
          // Add the selected element to promptContexts
          const elementData = (data as any).data;
          const contextId = `element-${Date.now()}-${Math.random()}`;
          addPromptContext({
            id: contextId,
            type: 'element',
            sectionId: elementData.sectionId,
            sectionType: elementData.sectionType,
            sectionIndex: elementData.sectionIndex,
            popupId: elementData.popupId,
            popupName: elementData.popupName,
            elementPath: elementData.elementPath,
            elementHTML: elementData.elementHTML,
            elementText: elementData.elementText,
            elementTag: elementData.elementTag,
            screenshot: elementData.screenshot,
          });

          // Exit visual edit mode
          setIsVisualEditMode(false);
          if (isEditable) {
            sendMessageToIframe({
              eventName: 'previewConfigUpdated',
              isEditable: previewMode ? false : true,
            });
          }
          sendMessageToIframe({ eventName: 'exitSelectionMode' });

          return;
        }

        const eventToChangeMap: {
          [key in EventName]: WebsiteUpdateType | undefined;
        } = {
          getData: undefined,
          generateSuggestions: undefined,
          addToFavorites: undefined,
          removeFromFavorites: undefined,
          ready: undefined,
          addToCatalog: undefined,
          removeFromCatalog: undefined,
          editGlobalSection: undefined,
          editHtmlAction: undefined,
          fixHtmlSectionError: undefined,
          cursorMove: undefined,

          changeDesign: 'changeDesign',
          addSectionBelow: 'addSectionBelow',
          textChanged: 'text',
          htmlElementChanged: 'htmlElementChanged',
          moveSectionDown: 'moveSectionDown',
          moveSectionUp: 'moveSectionUp',
          addSectionItem: 'addSectionItem',
          deleteSection: 'deleteSection',
          deleteSectionItem: 'deleteSectionItem',
          editSectionItem: 'editSectionItem',
          duplicateSectionItem: 'duplicateSectionItem',
          duplicateSection: 'duplicateSection',
          selectImage: 'selectImage',
          selectThumbnail: 'selectThumbnail',
          editSection: 'editSection',
          editForm: 'editForm',
          replaceForm: 'replaceForm',
          editButton: 'editButton',
          editImageSettings: 'editImageSettings',
          convertGlobalToLocal: 'convertGlobalToLocal',
          convertLocalToGlobal: 'convertLocalToGlobal',
          customValueMentionClick: 'customValueMentionClick',
          showPopup: undefined,
          hidePopup: undefined,
          popupUpdate: undefined,
        };
        const changeType = eventToChangeMap[data.eventName];

        if (!changeType) {
          console.warn('[WEBSITE_PREVIEW_EVENT] unknown event', data.eventName);
          return;
        }

        const wu = createWebsiteUpdate(
          dynamicPageId,
          websiteId,
          data.data.sectionId,
          data.data.propKey,
          data.data.content,
          changeType,
          data.data.subitems,
          website?.development
            ? JSON.parse(JSON.stringify(website?.development))
            : undefined,
        );

        if (!wu) {
          console.warn(
            '[WEBSITE_PREVIEW_EVENT] unable to create website update object',
            wu,
          );
          return;
        }

        handleWebsiteUpdateEvent(wu);
      } catch (e) {
        Sentry.captureException(e);
        console.warn('[WEBSITE_PREVIEW_EVENT] failed to parse event data', e);
      }
    },
    [
      dynamicPageId,
      id,
      createWebsiteUpdate,
      websiteId,
      website,
      handleWebsiteUpdateEvent,
      setReady,
      handleGetDataEvent,
      handleGenerateSuggestionsEvent,
      setAddFavoriteSectionModalOpen,
      setRemoveFavoriteSectionModalOpen,
      setAddSectionToCatalogModalOpen,
      setRemoveSectionFromCatalogModalOpen,
      setEditGlobalSectionModalOpen,
      setEditHtmlActionModalOpen,
      onCursorMove,
      sendMessage,
      addPromptContext,
      setIsVisualEditMode,
      isEditable,
      previewMode,
      sendMessageToIframe,
    ],
  );

  useEffect(() => {
    window.addEventListener('message', handlePreviewEvent);

    return () => {
      window.removeEventListener('message', handlePreviewEvent);
    };
  }, [handlePreviewEvent]);

  useEffect(() => {
    if (!ready) return;

    sendMessageToIframe({
      eventName: 'previewConfigUpdated',
      device,
      isEditable: isEditable ? (previewMode ? false : true) : false,
    });
  }, [ready, device, isEditable, previewMode, sendMessageToIframe]);

  return {
    handlePreviewEvent,
  };
}
